import { useCallback, useMemo } from 'react';
import { Control, FieldName, useController } from 'react-hook-form';

import { AnalyticalExaminationType, AnalyticalExaminationTypeQuery } from '@dreryk/drerykweb-medical-dictionaries-api';

import { CqrsQueryPerformResult, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import { Bean } from '@dreryk/react-common';
import { TextPattern } from '@dreryk/watermelon';

import { requiredValidator } from '../../../shared/common.utils';
import { ReferralToAnalyticalExaminationFormBean } from '../ReferralToAnalyticalExaminationForm.types';

export const analyticalExaminationTypeSearchCreator = (searchText: string | null) => async (): CqrsQueryPerformResult<
   AnalyticalExaminationType[]
> => {
   return performCqrsQuerySearch<AnalyticalExaminationType>(() => {
      const query = new AnalyticalExaminationTypeQuery();
      if (searchText) {
         query.name = TextPattern.anywhereIgnoreCase(searchText);
      }
      return query;
   });
};

export type AutosuggestSelectorReturnType<FIELD_TYPE extends Bean> = {
   addItem: (item: FIELD_TYPE) => void;
   removeItem: (item: FIELD_TYPE) => void;
   invalid: boolean;
   validate: () => void;
   selectedItems: FIELD_TYPE[];
};

export const useAnalyticalExaminationTypeSelector = (
   control: Control,
   fieldName: FieldName<ReferralToAnalyticalExaminationFormBean>
): AutosuggestSelectorReturnType<AnalyticalExaminationType> => {
   const controller = useController({
      name: fieldName,
      control: control,
      defaultValue: [],
      rules: {
         validate: requiredValidator,
      },
   });
   const {
      field: { onChange, value: selectedItems },
   } = controller;

   const addItem = useCallback(
      (item: AnalyticalExaminationType): void => {
         if (selectedItems.filter((i: AnalyticalExaminationType) => i.id === item.id).length > 0) {
            return;
         }
         onChange([item, ...selectedItems]);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedItems]
   );

   const removeItem = useCallback(
      (item: AnalyticalExaminationType): void => {
         onChange(selectedItems.filter((i: AnalyticalExaminationType) => i.id !== item.id));
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedItems]
   );

   const invalid = controller.meta.invalid && !selectedItems.length;

   const validate = useCallback(async (): Promise<void> => {
      await control.trigger(fieldName);
      // Exclude control
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [fieldName]);

   return useMemo(
      () => ({
         addItem: addItem,
         removeItem: removeItem,
         invalid: invalid,
         validate: validate,
         selectedItems: selectedItems,
      }),
      [addItem, removeItem, invalid, validate, selectedItems]
   );
};
